import { Ability } from '@casl/ability';
import { cAProvinces, statesOptions } from '@dispatch/shared/options';
import {
  Actions,
  Region,
  ResultStatus,
  Subjects,
} from '@dispatch/shared/types';
import {
  DrawerComponent,
  Form,
  RenderFuncProps,
  Section,
  SelectField,
  TextField,
} from '@dispatch/web/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { Grid } from '@mui/material';
import { isNull, isEmpty } from 'lodash';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

const schema = yup
  .object({
    name: yup.string().trim().required('Name is required'),
    country: yup.string().required('Country is required'),
    states: yup
      .array()
      .min(1, 'Must choose at least one state')
      .required('State is required'),
  })
  .required();

export type RegionsForm = {
  name: string;
  country: string;
  states: string[];
};

export type RegionsDetailsProps = {
  data: Region;
  status: ResultStatus;
  allRegions: Region[];
  onSave: (value: RegionsForm) => void;
  onDeactivate?: (value: Region) => void;
  onActivate?: (value: Region) => void;
  permissions: Ability<[Actions, Subjects]> | undefined;
};

export function RegionsDetails({
  data,
  onSave,
  onDeactivate,
  onActivate,
  status,
  allRegions,
  permissions,
}: RegionsDetailsProps) {
  const params = useParams();
  const regionId = params['regionId'];
  const navigate = useNavigate();
  const [statesUsed, setStatesUsed] = React.useState<string[]>();

  const formDefaults: RegionsForm = {
    name: '',
    states: [],
    country: '',
  };

  const {
    control: formControl,
    formState: { errors: formErrors, isDirty },
    handleSubmit,
    setValue,
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { ...formDefaults },
  });

  const hasPermissions = !!permissions?.can('set', 'regions');

  const handleDeactivation = () => {
    onDeactivate && onDeactivate(data);
  };
  const handleActivation = () => {
    onActivate && onActivate(data);
  };
  const handleSave = () => {
    handleSubmit(onSave)();

    if (regionId) {
      return reset(data);
    }
  };

  /**
   * remove states that have already been used to prevent adding a state that
   * has already been used, also allows a select all functionality without
   * worrying about selecting already used states
   */
  const removeUsed = React.useCallback(
    (options: { title: string; value: string }[]) => {
      return options.filter(
        (state: { title: string; value: string }) =>
          !statesUsed?.includes(state.value)
      );
    },
    [statesUsed]
  );

  React.useEffect(() => {
    if (data) {
      Object.entries(data)?.forEach(([key, value]: [unknown, string]) => {
        const currKey = key as
          | 'name'
          | 'country'
          | 'states'
          | `states.${number}`;
        setValue(currKey, value);
      });
    }

    // filter used states
    let used: string[] = [];
    allRegions?.forEach((region: Region) => {
      if (region.id !== regionId) {
        used = [...used, ...region.states];
      }
    });

    setStatesUsed(used);
  }, [data, setValue, allRegions, regionId]);

  const countryValue = watch('country');

  const renderOptions = (country: string) => {
    if (country === 'USA') {
      return removeUsed(statesOptions);
    } else if (country === 'CA') {
      return removeUsed(cAProvinces);
    }
    return [];
  };

  const onCountryChange = (value: unknown) => {
    const rawValue = value as { title: string; value: string };
    const country = rawValue.value;
    const states = watch('states');

    if (country !== countryValue && states.length > 0) {
      setValue('states', []);
    }
  };

  const formArgs = {
    render: ({ control, errors }: RenderFuncProps) => {
      return (
        <Section title="Region Information">
          <Grid container spacing={2} alignItems="center">
            <Grid size={6}>
              <TextField
                id="name"
                name="name"
                label="Region Name"
                required
                control={control}
                errors={errors}
                sx={{ width: '100%' }}
                disabled={!hasPermissions}
              />
            </Grid>
            <Grid data-test-id="country-dropdown" size={6}>
              <SelectField
                id={`country`}
                name={`country`}
                label="Country"
                onValueChange={onCountryChange}
                required
                control={control}
                errors={errors}
                error={errors['country']?.message ?? null}
                options={[
                  { title: 'United States', value: 'USA' },
                  { title: 'Canada', value: 'CA' },
                ]}
                readOnly={!hasPermissions}
              />
            </Grid>
          </Grid>
          <Grid
            container
            sx={{ margin: '10px 0' }}
            data-test-id="states-dropdown"
          >
            <Grid size={12}>
              {watch('country') !== '' ? (
                <SelectField
                  allowSelectAll
                  addScroll
                  id={`states`}
                  name={`states`}
                  label="Included States/Provinces"
                  required
                  control={control}
                  errors={errors}
                  multiple
                  error={errors['states']?.message ?? null}
                  options={renderOptions(watch('country') || data?.country)}
                  readOnly={!hasPermissions}
                />
              ) : null}
            </Grid>
          </Grid>
        </Section>
      );
    },
  };

  return (
    <DrawerComponent
      status={status}
      showButtons={hasPermissions}
      showDeactivateButton={isNull(data?.deactivatedAt) && !isEmpty(data)}
      onDeactivate={handleDeactivation}
      showActivateButton={!isNull(data?.deactivatedAt) && !isEmpty(data)}
      onActivate={handleActivation}
      onSave={handleSave}
      saveDisabled={!isDirty}
      onClose={() => navigate('/admin/regions')}
      title={data?.name || 'Add a New Region'}
      subtitle={data?.name && 'REGION'}
      isActive={!data?.deactivatedAt}
      sx={{ width: '55vw' }}
    >
      {data && (
        <Form<RegionsForm>
          {...formArgs}
          errors={formErrors}
          control={formControl}
          handleSubmit={handleSubmit}
        />
      )}
    </DrawerComponent>
  );
}
